import { useId } from 'react';
import { useTheme } from 'styled-components';
import Button from '@hellodarwin/core/lib/components/common/button';
import HdTag from '@hellodarwin/core/lib/components/common/hd-tag';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import type { AssetWithOwnersEntity } from '@hellodarwin/core/lib/features/entities/assets-entities';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Download from '@hellodarwin/icons/dist/icons/Download';
import File from '@hellodarwin/icons/dist/icons/File';
import GoogleDriveFolder from '@hellodarwin/icons/dist/icons/GoogleDriveFolder';
import type { ProgramDocumentRequirement } from '../../features/api/program-documents-api';
import {
  DocumentActions,
  DocumentDetails,
  DocumentDownloadButton,
  DocumentGroup,
  DocumentGroupTitle,
  DocumentHeader,
  DocumentIcon,
  DocumentName,
  DocumentRow,
  DocumentSection,
  DocumentSubtitle,
  RequiredDocumentList,
} from './styles';

const DownloadableFile = ({
  asset,
  onDownload,
}: {
  asset: AssetWithOwnersEntity;
  onDownload: RequiredDocumentsProps['onDownload'];
}) => {
  const { t } = useTranslations();
  const fileName = asset.display_name || asset.file_name;

  return (
    <DocumentDownloadButton
      type="button"
      aria-label={`${t('document-journey|documentDownload')} ${fileName}`}
      onClick={() => onDownload(asset)}
    >
      <span>{fileName}</span>
      <span aria-hidden="true">
        <Download size={16} />
      </span>
    </DocumentDownloadButton>
  );
};

export interface RequiredDocumentsProps {
  requirements: readonly ProgramDocumentRequirement[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onUpload: (requirement: ProgramDocumentRequirement) => void;
  onDownload: (asset: AssetWithOwnersEntity) => void;
}

const RequirementRow = ({
  requirement,
  onUpload,
  onDownload,
}: Pick<RequiredDocumentsProps, 'onUpload' | 'onDownload'> & {
  requirement: ProgramDocumentRequirement;
}) => {
  const { t } = useTranslations();
  const theme = useTheme();
  const bucket = requirement.kind === 'folder';
  const singleAsset =
    requirement.application_item?.item_asset_application?.file_response;
  const files =
    requirement.application_item?.asset_bucket_application?.file_responses ??
    [];
  const uploadLabel = t(
    bucket
      ? files.length > 0
        ? 'document-journey|documentManageFiles'
        : 'document-journey|documentAddFiles'
      : singleAsset
        ? 'document-journey|documentManageFile'
        : 'document-journey|dashboardWidgetUpload',
  );

  return (
    <DocumentRow aria-label={requirement.document_name}>
      <DocumentIcon $bucket={bucket}>
        {bucket ? <GoogleDriveFolder size={24} /> : <File size={24} />}
      </DocumentIcon>
      <DocumentDetails>
        <DocumentName>{requirement.document_name}</DocumentName>
        {singleAsset && !bucket ? (
          <DownloadableFile asset={singleAsset} onDownload={onDownload} />
        ) : (
          <DocumentSubtitle>
            {bucket
              ? t('document-journey|documentBucketHint')
              : t('document-journey|documentSingleHint')}
          </DocumentSubtitle>
        )}
        {bucket && files.length > 0
          ? files.map((asset) => (
              <DownloadableFile
                key={asset.asset_id}
                asset={asset}
                onDownload={onDownload}
              />
            ))
          : null}
      </DocumentDetails>
      <DocumentActions>
        <HdTag
          text={t(
            requirement.fulfilled
              ? 'document-journey|documentReceived'
              : 'document-journey|documentToUpload',
          )}
          color={
            requirement.fulfilled ? theme.colors.green_4 : theme.colors.purple_6
          }
          size="small"
        />
        <Button
          variant={singleAsset && !bucket ? 'outline' : 'navigation'}
          onClick={() => onUpload(requirement)}
        >
          {uploadLabel}
        </Button>
      </DocumentActions>
    </DocumentRow>
  );
};

export const RequiredDocuments = ({
  requirements,
  isLoading,
  isError,
  onRetry,
  onUpload,
  onDownload,
}: RequiredDocumentsProps) => {
  const { t } = useTranslations();
  const titleId = useId();
  const groups = [
    { kind: 'file', title: 'document-journey|documentIndividualFiles' },
    { kind: 'folder', title: 'document-journey|documentBuckets' },
  ] as const;
  return (
    <DocumentSection aria-labelledby={titleId} aria-busy={isLoading}>
      <DocumentHeader>
        <Typography elementTheme="h5" id={titleId}>
          {t('document-journey|requiredDocumentsTitle')}
        </Typography>
        <DocumentSubtitle>
          {t('document-journey|requiredDocumentsDescription')}
        </DocumentSubtitle>
      </DocumentHeader>
      {isError ? (
        <>
          <div role="alert">
            <Typography>{t('document-journey|documentsLoadError')}</Typography>
          </div>
          {onRetry ? (
            <Button onClick={onRetry}>
              {t('document-journey|documentsRetry')}
            </Button>
          ) : null}
        </>
      ) : isLoading ? (
        <Loading />
      ) : requirements.length === 0 ? (
        <Typography>{t('document-journey|requiredDocumentsEmpty')}</Typography>
      ) : (
        <>
          {groups.map(({ kind, title }) => {
            const items = requirements.filter(
              (requirement) => requirement.kind === kind,
            );
            return items.length > 0 ? (
              <DocumentGroup key={kind} role="group" aria-label={t(title)}>
                <DocumentGroupTitle>{t(title)}</DocumentGroupTitle>
                <RequiredDocumentList>
                  {items.map((requirement) => (
                    <RequirementRow
                      key={requirement.id}
                      requirement={requirement}
                      onUpload={onUpload}
                      onDownload={onDownload}
                    />
                  ))}
                </RequiredDocumentList>
              </DocumentGroup>
            ) : null;
          })}
        </>
      )}
    </DocumentSection>
  );
};
